'use client';
import { ProductCardContext } from '.';
import { useTranslations } from 'next-intl';
import { ComponentProps, useContext, useMemo } from 'react';

import { useSelectedCurrency } from '@/shared/lib/hooks/useSelectedCurrency';
import { Currency } from '@/shared/model/Currency';

import { useCartStore, getPriceValueTranslation } from '@/entities/cart';
import { Layout } from '@/shared/ui/layout';
import { ProductCardProps } from './types';

type Props = ComponentProps<typeof Layout> & Partial<Pick<ProductCardProps, 'priceValues'>>;
export const ProductCardPrice = ({ priceValues, media, ...props }: Props) => {
    const t = useTranslations();
    const context = useContext(ProductCardContext);
    const { hash, selectedVariantId, priceValues: contextPriceValues } = context;

    const getItemPrices = useCartStore((s) => s.getItemPrices);
    const [selectedCurrency] = useSelectedCurrency();
    const finalPriceValues = priceValues ?? contextPriceValues;

    const preferredPrice = useMemo(() => {
        // The cart store's price wins ONLY while it is denominated in the
        // currency the customer picked. It is computed from a snapshot that
        // carries its own currency, so right after a switch it still holds the
        // previous one — and taking it unconditionally is what left every card
        // reading "120 Kč" after switching to EUR, and again after a reload.
        //
        // Falling through is safe: `priceValues` is built with
        // `currency: selectedCurrency` by the card itself, so the fallback is
        // always in the right denomination.
        const prices = getItemPrices(selectedVariantId || hash);
        const itemPrice = prices?.itemPrice as { amount: number | string; currency: string } | undefined;
        if (itemPrice && itemPrice.currency === selectedCurrency) {
            return {
                amount: Number(itemPrice.amount),
                currency: itemPrice.currency as Currency,
            };
        }

        if (!finalPriceValues || !finalPriceValues.length) {
            return null;
        }

        // The price list carries one entry per currency the venue sells in, in
        // no particular order, so index 0 is whichever the backend happened to
        // return first — not the one the customer picked. Match on the selected
        // currency and keep index 0 only as the fallback for a product that has
        // no price in it.
        const inSelectedCurrency = finalPriceValues.find(
            (candidate: { amount: number | string; currency: string }) =>
                candidate.currency === selectedCurrency,
        );
        const price = inSelectedCurrency ?? finalPriceValues[0];

        return {
            amount: Number(price.amount),
            currency: price.currency as Currency,
        };
    }, [getItemPrices, selectedVariantId, hash, finalPriceValues, selectedCurrency]);

    if (!preferredPrice) {
        return null;
    }

    return (
        <Layout
            element="span"
            data-testid="product-card-price"
            media={{
                initial: {
                    fontWeight: 700,
                    fontSize: '1.25rem',
                },
                ...media,
            }}
            {...props}
        >
            {getPriceValueTranslation({
                amount: preferredPrice.amount,
                currency: preferredPrice.currency,
                t,
            })}
        </Layout>
    );
};
